import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import shopifyService from '../services/shopifyService';

const ShopifyContext = createContext(null);

// Cache expiry time in milliseconds (30 seconds for real-time sync)
const CACHE_EXPIRY_MS = 30 * 1000;

// Auto-refresh interval (30 seconds)
const AUTO_REFRESH_INTERVAL_MS = 30 * 1000;

// Version key to force cache invalidation when app updates
const CACHE_VERSION = 'v6';
const CACHE_VERSION_KEY = 'tpe_cache_version';

// Helper to check if cached data is still valid
const isValidCache = (timestamp) => {
  if (!timestamp) return false;
  return (Date.now() - timestamp) < CACHE_EXPIRY_MS;
};

// Check if cache version matches (force clear on version change)
const checkCacheVersion = () => {
  try {
    const storedVersion = localStorage.getItem(CACHE_VERSION_KEY);
    if (storedVersion !== CACHE_VERSION) {
      // Version mismatch - clear all cache
      console.log('[Shopify Sync] Cache version mismatch, clearing old cache...');
      clearProductCache();
      localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
};

// Helper to get cached data with timestamp validation
const getCachedData = (key) => {
  try {
    // First check cache version
    if (!checkCacheVersion()) {
      return null;
    }
    
    const cached = localStorage.getItem(key);
    const timestamp = localStorage.getItem(`${key}_ts`);
    if (cached && timestamp && isValidCache(parseInt(timestamp, 10))) {
      return JSON.parse(cached);
    }
    // Clear expired cache
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_ts`);
  } catch (e) {}
  return null;
};

// Helper to set cached data with timestamp
const setCachedData = (key, data) => {
  try {
    if (data && (Array.isArray(data) ? data.length > 0 : true)) {
      localStorage.setItem(key, JSON.stringify(data));
      localStorage.setItem(`${key}_ts`, Date.now().toString());
      localStorage.setItem(CACHE_VERSION_KEY, CACHE_VERSION);
    }
  } catch (e) {}
};

// Clear all product-related cache
const clearProductCache = () => {
  try {
    localStorage.removeItem('tpe_products');
    localStorage.removeItem('tpe_products_ts');
    localStorage.removeItem('tpe_collections');
    localStorage.removeItem('tpe_collections_ts');
    console.log('[Shopify Sync] Frontend cache cleared');
  } catch (e) {}
};

export const useShopify = () => {
  const context = useContext(ShopifyContext);
  if (!context) {
    throw new Error('useShopify must be used within a ShopifyProvider');
  }
  return context;
};

export const ShopifyProvider = ({ children }) => {
  const [cart, setCart] = useState(null);
  const [cartLoading, setCartLoading] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // Initialize with cached data only if not expired (for instant display)
  const [products, setProductsState] = useState(() => {
    return getCachedData('tpe_products') || [];
  });
  const [collections, setCollectionsState] = useState(() => {
    return getCachedData('tpe_collections') || [];
  });
  const [productsLoading, setProductsLoading] = useState(true); // Always start loading
  const [collectionsLoading, setCollectionsLoading] = useState(true);
  
  // Track last refresh time
  const lastRefreshRef = useRef(0);
  const isMountedRef = useRef(true);

  const setProducts = useCallback((prods) => {
    if (!isMountedRef.current) return;
    setProductsState(prods);
    setCachedData('tpe_products', prods);
    setLastSyncTime(new Date());
  }, []);
  
  const setCollections = useCallback((cols) => {
    if (!isMountedRef.current) return;
    setCollectionsState(cols);
    setCachedData('tpe_collections', cols);
  }, []);
  
  const [error, setError] = useState(null);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('shopifyCart');
    if (savedCart) {
      try {
        setCart(JSON.parse(savedCart));
      } catch (e) {
        localStorage.removeItem('shopifyCart');
      }
    }
  }, []);

  // Save cart to localStorage when it changes
  useEffect(() => {
    if (cart) {
      localStorage.setItem('shopifyCart', JSON.stringify(cart));
    }
  }, [cart]);

  // Fetch products on mount with retry
  useEffect(() => {
    isMountedRef.current = true;
    
    // Always fetch fresh data on mount (don't rely solely on cache)
    console.log('[Shopify Sync] Initial data fetch...');
    fetchProducts(3, false, true); // forceRefresh=true for initial load
    fetchCollections(3, false, true);
    
    // Set up periodic refresh for automatic sync with Shopify
    const refreshInterval = setInterval(() => {
      console.log('[Shopify Sync] Auto-refreshing all product data...');
      fetchProducts(1, true, true); // Silent mode, force refresh
      fetchCollections(1, true, true);
    }, AUTO_REFRESH_INTERVAL_MS);
    
    // Refresh when tab becomes visible again (user returns to site)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
        // Refresh if more than 30 seconds since last refresh
        if (timeSinceLastRefresh > 30000) {
          console.log('[Shopify Sync] Tab visible, refreshing data...');
          fetchProducts(1, true, true);
          fetchCollections(1, true, true);
        }
      }
    };
    
    // Refresh on focus (when user clicks on the window)
    const handleFocus = () => {
      const timeSinceLastRefresh = Date.now() - lastRefreshRef.current;
      if (timeSinceLastRefresh > 30000) {
        console.log('[Shopify Sync] Window focused, refreshing data...');
        fetchProducts(1, true, true);
        fetchCollections(1, true, true);
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    
    return () => {
      isMountedRef.current = false;
      clearInterval(refreshInterval);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProducts = async (retries = 3, silent = false, forceRefresh = false) => {
    if (!isMountedRef.current) return;
    
    try {
      if (!silent) {
        if (products.length === 0) setProductsLoading(true);
        setIsSyncing(true);
      }
      setError(null);
      
      // Clear backend cache if forcing refresh
      if (forceRefresh) {
        await shopifyService.refreshCache().catch(() => {});
      }
      
      const data = await shopifyService.getProducts(250);
      const prods = data.products || [];
      
      if (prods.length > 0) {
        setProducts(prods);
        lastRefreshRef.current = Date.now();
        console.log(`[Shopify Sync] Synced ${prods.length} products`);
        if (!silent) {
          setProductsLoading(false);
          setIsSyncing(false);
        }
      } else if (retries > 0) {
        setTimeout(() => fetchProducts(retries - 1, silent, false), 3000);
      } else {
        if (!silent) {
          setProductsLoading(false);
          setIsSyncing(false);
        }
      }
    } catch (err) {
      console.error('[Shopify Sync] Error fetching products:', err);
      if (!silent) setError(err.message);
      if (retries > 0) {
        const delay = (4 - retries) * 3000;
        setTimeout(() => fetchProducts(retries - 1, silent, false), delay);
      } else {
        if (!silent) {
          setProductsLoading(false);
          setIsSyncing(false);
        }
      }
    }
  };

  const fetchCollections = async (retries = 3, silent = false, forceRefresh = false) => {
    if (!isMountedRef.current) return;
    
    try {
      if (!silent && collections.length === 0) setCollectionsLoading(true);
      const data = await shopifyService.getCollections(50);
      const cols = data.collections || [];
      if (cols.length > 0) {
        setCollections(cols);
        console.log(`[Shopify Sync] Synced ${cols.length} collections`);
        if (!silent) setCollectionsLoading(false);
      } else if (retries > 0) {
        setTimeout(() => fetchCollections(retries - 1, silent, false), 3000);
      } else {
        if (!silent) setCollectionsLoading(false);
      }
    } catch (err) {
      console.error('[Shopify Sync] Error fetching collections:', err);
      if (!silent) setError(err.message);
      if (retries > 0) {
        const delay = (4 - retries) * 3000;
        setTimeout(() => fetchCollections(retries - 1, silent, false), delay);
      } else {
        if (!silent) setCollectionsLoading(false);
      }
    }
  };

  // Fully client-side cart management (works without backend)
  const addToCart = useCallback(async (product, variant) => {
    setCartLoading(true);
    try {
      const item = {
        variantId: variant.id,
        productId: product.id,
        quantity: 1,
        title: variant.title || 'Default',
        price: variant.price,
        image: variant.image?.url || product.featuredImage?.url || product.images?.[0]?.url,
        productTitle: product.title,
        productHandle: product.handle,
        selectedOptions: variant.selectedOptions || []
      };

      setCart(prevCart => {
        const lines = prevCart?.lines ? [...prevCart.lines] : [];
        const existingIdx = lines.findIndex(l => l.merchandise.id === item.variantId);

        if (existingIdx >= 0) {
          lines[existingIdx] = { ...lines[existingIdx], quantity: lines[existingIdx].quantity + item.quantity };
        } else {
          lines.push({
            id: `line-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
            quantity: item.quantity,
            merchandise: {
              id: item.variantId,
              title: item.title,
              price: item.price,
              currencyCode: 'USD',
              image: item.image ? { url: item.image, altText: item.productTitle } : null,
              productTitle: item.productTitle,
              productHandle: item.productHandle,
              productImage: item.image ? { url: item.image, altText: item.productTitle } : null,
              selectedOptions: item.selectedOptions
            }
          });
        }

        const totalQuantity = lines.reduce((sum, l) => sum + l.quantity, 0);
        const subtotal = lines.reduce((sum, l) => sum + l.quantity * l.merchandise.price, 0);

        return {
          id: prevCart?.id || `cart-${Date.now()}`,
          checkoutUrl: shopifyService.buildCheckoutUrl(lines),
          totalQuantity,
          subtotal,
          total: subtotal,
          tax: 0,
          currencyCode: 'USD',
          lines
        };
      });

      window.dispatchEvent(new Event('cartUpdated'));
    } finally {
      setCartLoading(false);
    }
  }, []);

  const updateCartLine = useCallback(async (lineId, quantity) => {
    setCartLoading(true);
    try {
      setCart(prevCart => {
        if (!prevCart) return prevCart;
        let lines = [...prevCart.lines];

        if (quantity <= 0) {
          lines = lines.filter(l => l.id !== lineId);
        } else {
          lines = lines.map(l => l.id === lineId ? { ...l, quantity } : l);
        }

        const totalQuantity = lines.reduce((sum, l) => sum + l.quantity, 0);
        const subtotal = lines.reduce((sum, l) => sum + l.quantity * l.merchandise.price, 0);

        return {
          ...prevCart,
          checkoutUrl: shopifyService.buildCheckoutUrl(lines),
          totalQuantity,
          subtotal,
          total: subtotal,
          lines
        };
      });

      window.dispatchEvent(new Event('cartUpdated'));
    } finally {
      setCartLoading(false);
    }
  }, []);

  const removeFromCart = useCallback(async (lineId) => {
    setCartLoading(true);
    try {
      setCart(prevCart => {
        if (!prevCart) return prevCart;
        const lines = prevCart.lines.filter(l => l.id !== lineId);
        const totalQuantity = lines.reduce((sum, l) => sum + l.quantity, 0);
        const subtotal = lines.reduce((sum, l) => sum + l.quantity * l.merchandise.price, 0);

        return {
          ...prevCart,
          checkoutUrl: shopifyService.buildCheckoutUrl(lines),
          totalQuantity,
          subtotal,
          total: subtotal,
          lines
        };
      });

      window.dispatchEvent(new Event('cartUpdated'));
    } finally {
      setCartLoading(false);
    }
  }, []);

  const checkout = useCallback(() => {
    if (cart?.checkoutUrl) {
      window.open(cart.checkoutUrl, '_blank');
    }
  }, [cart]);

  const clearCart = useCallback(() => {
    localStorage.removeItem('shopifyCart');
    setCart(null);
    window.dispatchEvent(new Event('cartUpdated'));
  }, []);

  const getProductByHandle = useCallback(async (handle, fresh = true) => {
    // Always get fresh data for individual product pages to ensure inventory accuracy
    return await shopifyService.getProductByHandle(handle, fresh);
  }, []);

  const getCollectionByHandle = useCallback(async (handle) => {
    return await shopifyService.getCollectionByHandle(handle);
  }, []);

  const getBestSellers = useCallback(() => {
    return products.filter(p => p.isBestSeller || p.tags?.some(t =>
      t.toLowerCase().includes('best') || t.toLowerCase().includes('seller')
    ));
  }, [products]);

  const getNewArrivals = useCallback(() => {
    return products.filter(p => p.isNew || p.tags?.some(t =>
      t.toLowerCase().includes('new')
    ));
  }, [products]);

  const getProductsByCollection = useCallback((collectionHandle) => {
    const collection = collections.find(c => c.handle === collectionHandle);
    return collection?.products || [];
  }, [collections]);

  // Get products by Shopify productType
  const getProductsByType = useCallback((productType) => {
    return products.filter(p => 
      (p.productType || '').toLowerCase() === productType.toLowerCase()
    );
  }, [products]);

  // Get all unique product types from Shopify
  const getProductTypes = useCallback(() => {
    const types = new Set();
    products.forEach(p => {
      if (p.productType) types.add(p.productType);
    });
    return Array.from(types).sort();
  }, [products]);

  // Get all unique tags from products
  const getAllTags = useCallback(() => {
    const tags = new Set();
    products.forEach(p => {
      (p.tags || []).forEach(t => tags.add(t));
    });
    return Array.from(tags).sort();
  }, [products]);

  // Get products by tag
  const getProductsByTag = useCallback((tag) => {
    return products.filter(p => 
      (p.tags || []).some(t => t.toLowerCase() === tag.toLowerCase())
    );
  }, [products]);

  // Force refresh that clears all caches (backend and frontend)
  const forceRefreshAll = useCallback(async () => {
    console.log('[Shopify Sync] Force refresh initiated...');
    setIsSyncing(true);
    
    try {
      // Clear all frontend cache
      clearProductCache();
      
      // Clear backend cache first
      await shopifyService.refreshCache();
      
      // Fetch fresh data from Shopify
      await Promise.all([
        fetchProducts(3, false, false), // Already cleared cache, don't need to again
        fetchCollections(3, false, false)
      ]);
      
      console.log('[Shopify Sync] Force refresh complete');
      return true;
    } catch (err) {
      console.error('[Shopify Sync] Error during force refresh:', err);
      setIsSyncing(false);
      return false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cartItemCount = cart?.totalQuantity || 0;

  const value = {
    cart,
    cartLoading,
    cartItemCount,
    addToCart,
    updateCartLine,
    removeFromCart,
    checkout,
    clearCart,
    products,
    productsLoading,
    getProductByHandle,
    getBestSellers,
    getNewArrivals,
    getProductsByType,
    getProductTypes,
    getAllTags,
    getProductsByTag,
    collections,
    collectionsLoading,
    getCollectionByHandle,
    getProductsByCollection,
    error,
    // Sync related
    isSyncing,
    lastSyncTime,
    refreshProducts: () => fetchProducts(1, false, true),
    refreshCollections: () => fetchCollections(1, false, true),
    forceRefreshAll
  };

  return (
    <ShopifyContext.Provider value={value}>
      {children}
    </ShopifyContext.Provider>
  );
};

export default ShopifyContext;
